import { localStorageService } from '@services/storage/localStorage.js';
import { STORAGE_KEYS } from '@config/constants.js';
import { translations } from './translations.js';

const SUPPORTED_LOCALES = ['us', 'tr'];
const LOCALE_TO_LANGUAGE = {
  us: 'en',
  tr: 'tr',
};
const DEFAULT_LOCALE = 'us';

let currentLocale = DEFAULT_LOCALE;
const listeners = new Set();

function normalizeLocale(locale) {
  const normalized = String(locale || '').toLowerCase();
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
}

export function getLocaleFromPath(pathname = '/') {
  const [firstSegment] = String(pathname).split('/').filter(Boolean);
  return SUPPORTED_LOCALES.includes(firstSegment) ? firstSegment : null;
}

export function stripLocaleFromPath(pathname = '/') {
  const parts = String(pathname).split('/').filter(Boolean);
  if (parts.length > 0 && SUPPORTED_LOCALES.includes(parts[0])) {
    parts.shift();
  }
  return parts.length === 0 ? '/' : `/${parts.join('/')}`;
}

export function localizedPath(pathname = '/', locale = currentLocale) {
  const safeLocale = normalizeLocale(locale);
  const raw = String(pathname || '/');
  const [pathOnly] = raw.split('?');
  const basePath = stripLocaleFromPath(pathOnly);
  const query = raw.includes('?') ? `?${raw.split('?').slice(1).join('?')}` : '';
  return basePath === '/' ? `/${safeLocale}${query}` : `/${safeLocale}${basePath}${query}`;
}

function detectLocaleFromBrowser() {
  const lang = String(navigator.language || '').toLowerCase();
  if (lang.startsWith('tr')) return 'tr';

  const tz = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
  if (tz.includes('istanbul')) return 'tr';

  return DEFAULT_LOCALE;
}

function getStoredLocale() {
  const stored = localStorageService.get(STORAGE_KEYS.APP_LOCALE, null);
  return stored && SUPPORTED_LOCALES.includes(stored) ? stored : null;
}

function getLanguageCode(locale = currentLocale) {
  return LOCALE_TO_LANGUAGE[normalizeLocale(locale)];
}

export function getLocale() {
  return currentLocale;
}

export function getLanguage() {
  return getLanguageCode(currentLocale);
}

export function setLocale(locale, { persist = true } = {}) {
  const nextLocale = normalizeLocale(locale);
  if (nextLocale === currentLocale) return false;
  currentLocale = nextLocale;

  if (persist) {
    localStorageService.set(STORAGE_KEYS.APP_LOCALE, nextLocale);
  }

  document.documentElement.lang = getLanguageCode(nextLocale);
  listeners.forEach((listener) => listener(nextLocale));
  return true;
}

export function initLocale(pathname = window.location.pathname) {
  const localeFromUrl = getLocaleFromPath(pathname);
  const storedLocale = getStoredLocale();
  const detectedLocale = detectLocaleFromBrowser();
  const shouldPreferStoredLocale =
    Boolean(storedLocale) && localeFromUrl === DEFAULT_LOCALE && storedLocale !== DEFAULT_LOCALE;
  const initialLocale = shouldPreferStoredLocale
    ? storedLocale
    : (localeFromUrl || storedLocale || detectedLocale || DEFAULT_LOCALE);

  currentLocale = normalizeLocale(initialLocale);
  localStorageService.set(STORAGE_KEYS.APP_LOCALE, currentLocale);
  document.documentElement.lang = getLanguageCode(currentLocale);
  return currentLocale;
}

export function t(key, fallback = '') {
  const lang = getLanguageCode(currentLocale);
  const chunks = String(key).split('.');
  const fromLocale = chunks.reduce((acc, chunk) => (acc && acc[chunk] !== undefined ? acc[chunk] : undefined), translations[lang]);
  if (fromLocale !== undefined) return fromLocale;

  const fromDefault = chunks.reduce((acc, chunk) => (acc && acc[chunk] !== undefined ? acc[chunk] : undefined), translations.en);
  if (fromDefault !== undefined) return fromDefault;
  return fallback || key;
}

export function onLocaleChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const localeConfig = {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
};
